import { z } from 'zod';
import { esquemaContrasena, esquemaCorreo } from './auth';

/**
 * Registro de un trabajador y control de acceso (D-022, D-023).
 *
 * El trabajador se registra solo con el código de su empresa, verifica su
 * correo, y queda pendiente hasta que Copathy apruebe.
 */

export const ESTADOS_SOLICITUD = ['sin_verificar', 'pendiente', 'aprobada', 'rechazada'] as const;
export type EstadoSolicitud = (typeof ESTADOS_SOLICITUD)[number];

/**
 * Código de registro de la empresa.
 *
 * Se normaliza a mayúsculas y sin espacios: la gente lo copia de un mensaje de
 * WhatsApp y llega con espacios al final y en minúsculas.
 */
export const esquemaCodigoEmpresa = z
  .string()
  .trim()
  .min(4, 'El código de tu empresa es demasiado corto.')
  .max(40, 'El código de tu empresa es demasiado largo.')
  .transform((v) => v.toUpperCase().replace(/\s+/g, ''));

/**
 * La edad en años a partir de `aaaa-mm-dd`, o `null` si la fecha no sirve.
 *
 * Vive en `shared/` porque la usan las dos puntas: el contrato para validar el
 * rango, y la app para **mostrarle la edad a la persona** debajo de la fecha —
 * que es como se da cuenta de que escribió 1892 en vez de 1982.
 *
 * ⚠️ Resta por componentes y no por milisegundos: dividir la diferencia entre
 * 365,25 días se equivoca en los cumpleaños y en los años bisiestos, y acá el
 * número se le muestra a alguien que lo va a comparar con el suyo.
 */
export function edadEn(fechaISO: string, hoy: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fechaISO.trim());
  if (!m) return null;
  const [, a, mes, d] = m;
  const ano = Number(a);
  const mesN = Number(mes);
  const diaN = Number(d);
  if (mesN < 1 || mesN > 12 || diaN < 1 || diaN > 31) return null;

  // Que la fecha exista de verdad: el 31 de febrero se normaliza solo y pasaría.
  const fecha = new Date(Date.UTC(ano, mesN - 1, diaN));
  if (
    fecha.getUTCFullYear() !== ano ||
    fecha.getUTCMonth() !== mesN - 1 ||
    fecha.getUTCDate() !== diaN
  ) {
    return null;
  }

  let anos = hoy.getFullYear() - ano;
  const cumplioEsteAno =
    hoy.getMonth() + 1 > mesN || (hoy.getMonth() + 1 === mesN && hoy.getDate() >= diaN);
  if (!cumplioEsteAno) anos -= 1;
  return anos;
}

/** Las cuatro opciones. Obligatorio elegir una desde D-083. */
export const GENEROS = ['femenino', 'masculino', 'otro', 'prefiero_no_decir'] as const;
export type Genero = (typeof GENEROS)[number];

export const GENERO: Record<Genero, string> = {
  femenino: 'Femenino',
  masculino: 'Masculino',
  otro: 'Otro',
  prefiero_no_decir: 'Prefiero no decirlo',
};

/**
 * Comprobar un código de empresa antes de avanzar en el registro (D-084).
 *
 * La respuesta trae **solo el nombre** de la empresa, nunca su id: este endpoint
 * es público y el id es lo que después filtra todo.
 */
export const esquemaConsultaCodigo = z.object({
  codigo: esquemaCodigoEmpresa,
});
export type EntradaConsultaCodigo = z.infer<typeof esquemaConsultaCodigo>;

export const esquemaRespuestaCodigo = z.object({
  valido: z.boolean(),
  empresaNombre: z.string().optional(),
});
export type RespuestaCodigo = z.infer<typeof esquemaRespuestaCodigo>;

export const esquemaRegistro = z.object({
  /**
   * ⚠️ **Opcional desde D-077.** Si la persona no lo tiene o no le funciona, el
   * registro NO se trunca: declara su empresa por escrito y Copathy la asigna al
   * aprobar. Uno de los dos tiene que venir — lo comprueba el `.refine` de abajo
   * Y el CHECK de la migración 021.
   */
  codigoEmpresa: esquemaCodigoEmpresa.optional(),
  /** Lo que la persona escribió cuando no tenía el código. Texto libre. */
  empresaDeclarada: z.string().trim().min(3, 'Escribe el nombre de tu empresa.').max(160).optional(),
  nombre: z
    .string()
    .trim()
    .min(3, 'Escribe tu nombre completo.')
    .max(120, 'El nombre es demasiado largo.'),
  correo: esquemaCorreo,
  contrasena: esquemaContrasena,
  /*
    Cargo y teléfono dejan de ser opcionales (D-075): el cargo es unidad de
    análisis de los informes, y el teléfono es por dónde Copathy contacta de
    verdad —WhatsApp—, así que pedirlo «si quieres» era pedirlo para nada.
  */
  cargo: z.string().trim().min(2, 'Escribe tu cargo.').max(120),
  /** Con prefijo: `+58 4141234567`. Lo compone `componerTelefono` de shared. */
  telefono: z.string().trim().min(8, 'Escribe tu teléfono con el código de país.').max(40),
  area: z.string().trim().max(120).optional(),
  /**
   * aaaa-mm-dd. **Obligatoria desde D-083.**
   *
   * El rango se comprueba acá y no solo en la pantalla: 16 años es el piso
   * laboral razonable y 100 el techo de lo verosímil. Sin esto, un dedo torpe
   * escribe 2205 y ese registro entra a los agregados como una persona de
   * menos-179 años.
   */
  fechaNacimiento: z
    .string()
    // El `.min(1)` va PRIMERO a propósito: los checks corren en orden y la
    // pantalla muestra el primer mensaje. Sin esto, un campo vacío decía «la
    // fecha va como aaaa-mm-dd», que es hablarle de formato a quien no escribió
    // nada.
    .min(1, 'Elige tu fecha de nacimiento.')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha va como aaaa-mm-dd.')
    .refine((v) => {
      const anos = edadEn(v);
      return anos !== null && anos >= 16 && anos <= 100;
    }, 'Revisa la fecha: la edad que sale no es posible.'),
  /*
    ⚠️ Con `errorMap`, porque sin él Zod dice «Required» —en inglés— y eso llega
    tal cual a la pantalla. Lo cazó abrir el formulario y darle a enviar vacío:
    ni `tsc` ni las pruebas miran el idioma de un mensaje.
  */
  genero: z.enum(GENEROS, { errorMap: () => ({ message: 'Elige una opción.' }) }),
  /** ISO alpha-2 en minúscula, igual que `empresas.pais`. */
  nacionalidad: z.string().trim().length(2, 'Elige tu nacionalidad.').toLowerCase(),
  paisResidencia: z.string().trim().length(2, 'Elige tu país.').toLowerCase(),
  estadoRegion: z.string().trim().min(2, 'Escribe tu estado o provincia.').max(120),
  ciudad: z.string().trim().min(2, 'Escribe tu ciudad.').max(120),
  /**
   * La foto, en base64 (`data:image/...;base64,...`).
   *
   * ⚠️ Viaja en el cuerpo del registro y no por una subida aparte porque quien se
   * registra **no tiene sesión**: un endpoint público que acepta archivos es un
   * disco lleno esperando. Acá el tope de tamaño lo pone el propio contrato, y el
   * límite por IP del registro ya cubre el abuso repetido. La app la reduce a
   * 512 px antes de mandarla; el servidor la reprocesa igual con `sharp`.
   */
  foto: z
    .string()
    .startsWith('data:image/', 'La foto no llegó en un formato que entendamos.')
    .max(700_000, 'La foto es muy grande. Toma otra o recórtala.')
    .optional(),
})
  .refine(
    (v) => !!v.codigoEmpresa || !!v.empresaDeclarada,
    { message: 'Hace falta el código de tu empresa, o su nombre.', path: ['codigoEmpresa'] },
  );
export type EntradaRegistro = z.infer<typeof esquemaRegistro>;

/**
 * Recuperar la contraseña **con el código por correo** de la 020 (D-078).
 *
 * ⚠️ No confundir con `esquemaRestablecer` de `auth.ts`, que es **por token de
 * enlace** — el mecanismo pensado para la web y todavía sin endpoint. Son dos
 * caminos a lo mismo, como pasa con la verificación del correo, y por eso el
 * nombre lleva `PorCodigo`: dos esquemas llamados igual fue justo el error que
 * `tsc` cazó al escribir esto.
 */
export const esquemaRestablecerPorCodigo = z.object({
  correo: esquemaCorreo,
  codigo: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine((v) => /^\d{6}$/.test(v), 'El código son 6 dígitos.'),
  contrasena: esquemaContrasena,
});
export type EntradaRestablecerPorCodigo = z.infer<typeof esquemaRestablecerPorCodigo>;

export const esquemaVerificar = z.object({
  token: z.string().min(20).max(200),
});

/**
 * ── Verificación del correo con CÓDIGO, para la app móvil (D-073) ──
 *
 * El flujo del enlace sigue vivo para la web. Este es el de la app: se pide el
 * código, se teclea, y **solo entonces** se manda la solicitud. Sin salir de la
 * aplicación en ningún momento.
 */
export const esquemaPedirCodigo = z.object({
  correo: esquemaCorreo,
});
export type EntradaPedirCodigo = z.infer<typeof esquemaPedirCodigo>;

/** Seis dígitos. Se limpian espacios y guiones: la gente los teclea o los pega. */
export const esquemaConfirmarCodigo = z.object({
  correo: esquemaCorreo,
  codigo: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine((v) => /^\d{6}$/.test(v), 'El código son 6 dígitos.'),
});
export type EntradaConfirmarCodigo = z.infer<typeof esquemaConfirmarCodigo>;

export const esquemaReenviar = z.object({
  correo: esquemaCorreo,
});

export const esquemaAprobar = z.object({
  /** Mapeo al catálogo de la empresa (D-013). Opcionales: se pueden completar después. */
  areaId: z.number().int().positive().optional(),
  cargoId: z.number().int().positive().optional(),
});
export type EntradaAprobar = z.infer<typeof esquemaAprobar>;

export const esquemaRechazar = z.object({
  motivo: z
    .string()
    .trim()
    .min(5, 'Escribe el motivo: le llega a la persona y sin él no sabe qué corregir.')
    .max(500),
});
export type EntradaRechazar = z.infer<typeof esquemaRechazar>;

export const esquemaFiltroSolicitudes = z.object({
  estado: z.enum(ESTADOS_SOLICITUD).optional(),
  empresaId: z.coerce.number().int().positive().optional(),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});

/** Lo que ve Copathy en la cola. Sin la contraseña, obviamente. */
export const esquemaSolicitud = z.object({
  id: z.number().int().positive(),
  empresaId: z.number().int().positive(),
  empresaNombre: z.string(),
  nombre: z.string(),
  correo: z.string(),
  cargoTexto: z.string().nullable(),
  areaTexto: z.string().nullable(),
  telefono: z.string().nullable(),
  estado: z.enum(ESTADOS_SOLICITUD),
  verificadoEn: z.string().nullable(),
  creadoEn: z.string(),
  decididaEn: z.string().nullable(),
  decididaPorNombre: z.string().nullable(),
  motivoRechazo: z.string().nullable(),
});
export type Solicitud = z.infer<typeof esquemaSolicitud>;

/**
 * `correoVerificado` en `true` significa que la solicitud ya quedó **pendiente de
 * aprobación**: el correo se verificó con código antes de mandarla (D-073). La
 * app usa ese dato para decir «ya está, espera la autorización» en vez de «revisa
 * tu correo».
 */
export const esquemaRespuestaRegistro = z.object({
  empresaNombre: z.string(),
  correoVerificado: z.boolean().optional(),
  /** Solo en dev: el enlace que iría por correo, para poder probar sin SMTP. */
  enlaceDev: z.string().optional(),
});
export type RespuestaRegistro = z.infer<typeof esquemaRespuestaRegistro>;
